import React from 'react';

import { request } from './base';
import { ContentTypes } from '@/helpers/api';
import { snakify } from '@/helpers/common';
import { CurrencyCode } from '@/helpers/currency';
import { DEMO_CURRENCY } from '@/helpers/variables';

export interface SubGameCategory {
  categoryId: string;
  title: string;
  description: string;
  order: number;
}
export interface GameCategory {
  id: string;
  title: React.ReactNode;
  gamesCount: number;
  subCategories: SubGameCategory[];
}

export enum Category {
  ALL = 'all',
  TRENDING = 'trending',
  SLOTS = 'slots',
  NEW = 'new',
  INSTANT_WINS = 'instant_wins',
  BONUS_BUY = 'bonus_buy',
  BTC = 'btc',
  TABLES = 'tables',
  LIVE = 'live',
  JACKPOT = 'jackpot',
  CASUAL = 'casual',
  EXCLUSIVE = 'exclusive',
  TOP = 'top',
  TOP_GAME = '_topgame',
  WAGERING = 'wagering',
  LEGENDS_AU = 'legends_au',
  LEGENDS_CA = 'legends_ca',
  LEGENDS_NZ = 'legends_nz',
  LEGENDS_DE = 'legends_de',
  LEGENDS_NO = 'legends_no',
  LEGENDS_CH = 'legends_ch',
  LEGENDS_EN = 'legends_en',
}

export interface GameD {
  title: string;
  provider: string;
  collections: {
    [providerId: string]: number;
  };
  real: Real;
  demo?: string;
}

export type Real = {
  [currencyCode: string]: {
    id: number;
    jackpot: number;
  };
};

export type GamePackObject = {
  [id: string]: GameD;
};

export type GameV1 = GameD & {
  id: string;
  fullPath: string;
  seoPath: string;
  listCollections: string[];
  primeGame?: boolean;
  currencies: Real;
};

export interface Provider {
  id: string;
  title: string;
}

export const gameCategories = (lng: string, baseUrl?: string) =>
  request<GameCategory[]>({
    url: `games/collections?l=${lng}`,
    baseUrl,
  });

export const providers = (baseUrl?: string) =>
  request<Provider[]>({
    url: `games/providers`,
    baseUrl,
  });

export namespace GamesFilter {
  export interface Body {
    device?: Device;
    currencies: CurrencyCode[] | (typeof DEMO_CURRENCY)[];
    pageSize?: number; // MIN 1 MAX 100
    page?: number;
    withoutTerritorialRestrictions?: boolean;
    filter?: Filter;
    sort?: Sort;
  }

  export interface BodySelectBySEOTitles {
    gameSeoTitles: string[];
    device?: Device;
  }

  export interface BodySelect {
    gameIds: string[];
    device?: Device;
  }

  export interface ResponseSelect {
    [gameId: string]: GameV2.Game;
  }

  export type Device = 'desktop' | 'mobile';

  export interface Filter {
    currencies?: CurrencyCode[];
    providers?: string[];
    volatilityRating?: VolatilityRating[];
    lines?: number;
    ways?: number;
    hitRate?: number;
    payout?: number;
    title?: string;
    categories?: Categories;
  }

  export type VolatilityRating =
    | 'low'
    | 'low-medium'
    | 'medium'
    | 'medium-high'
    | 'high'
    | 'very-high';

  export interface Categories {
    identifiers: string[];
    strategy?: Strategy;
  }

  export type Strategy = 'OR' | 'AND';

  export interface Sort {
    type: SortType;
    direction: Direction;
    topCategories?: string[];
  }

  export type SortType = 'global' | 'novelty' | 'popularity';

  export type Direction = 'ASC' | 'DESC';

  export interface Response {
    data: GameV2.Game[];
    pagination: Pagination;
  }

  export interface Pagination {
    currentPage: number;
    nextPage: number;
    prevPage: number | null;
    totalPages: number;
    totalCount: number;
  }
}

export namespace GameV2 {
  export interface Game {
    title: string;
    provider: string;
    seoTitle: string;
    identifier: string;
    lines: number;
    ways: number | null;
    volatilityRating: GamesFilter.VolatilityRating;
    hitRate: string | null;
    payout: string;
    currencies: Currencies;
    unfinishedGamesFor: string[] | null;
    categories: string[];
    devices: GamesFilter.Device[];
    uniqSeoTitle: boolean;
  }

  export interface Currencies {
    [currencyCode: string]: Currency;
  }

  export interface Currency {
    id: number;
    jackpot: number;
  }
}

export const gamesFilter = (body?: GamesFilter.Body) =>
  request<GamesFilter.Response>({
    method: 'POST',
    url: 'games_filter',
    data: snakify({ ...body }),
    accept: ContentTypes.vndJsonV2,
  });

export const gamesFilterSelect = (body: GamesFilter.BodySelect) =>
  request<GamesFilter.ResponseSelect>({
    method: 'POST',
    url: 'games_filter/select',
    data: snakify({ ...body }),
    accept: ContentTypes.vndJsonV2,
  });

export const gamesFilterSelectBySEOTitle = (
  body: GamesFilter.BodySelectBySEOTitles
) =>
  request<GamesFilter.ResponseSelect>({
    method: 'POST',
    url: 'games_filter/select_by_seo_titles',
    data: snakify({ ...body }),
    accept: ContentTypes.vndJsonV2,
  });

export const gamesFilterByJackpotCategory = (jackpotCategoryId: string) =>
  request<GamesFilter.ResponseSelect>({
    method: 'POST',
    url: 'games_filter/select_by_jackpot_category',
    data: snakify({ jackpotCategoryId }),
    accept: ContentTypes.vndJsonV2,
  });

export interface RecentGame {
  identifier: string;
  lastActivityAt: string;
}

export const recentGames = () =>
  request<RecentGame[]>({ url: 'player/played_games' });

export const favoriteGames = () =>
  request<GameV2.Game[]>({
    url: 'player/favorite_games',
    accept: ContentTypes.vndJsonV2,
  });

export const favoriteGamesIds = () =>
  request<number[]>({
    url: 'player/favorite_games',
  });

export const addFavoriteGame = (currencyCode: number) =>
  request<null>({
    method: 'PUT',
    url: `player/favorite_games/${currencyCode}`,
  });

export const deleteFavoriteGame = (currencyCode: number) =>
  request<null>({
    method: 'DELETE',
    url: `player/favorite_games/${currencyCode}`,
  });
